import { useState } from 'react';
import { LISTE_ALLE, TablePager, useSeiten } from '../components/TablePager';
import { FormError } from '../components/FormError';
import { EuroInput } from '../components/EuroInput';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import {
  ApiError,
  useOrdersControllerList,
  useOrdersControllerCreate,
  useCustomersControllerUpdateUnit,
  useCustomersControllerList,
  useCustomersControllerUnits,
  getOrdersControllerListQueryKey,
} from '@werkbank/shared';
import { ORDER_STATUS_LABELS, selectOptions, formatDateDE } from '../lib/format';
import { useNeuParam } from '../lib/useNeuParam';
import { holdReasonLabel } from '../lib/holdLabels';
import { COMMISSION_TYPE_OPTIONS, DEFAULT_COMMISSION_TYPE } from '../lib/commissionLabels';
import { StatusBadge } from '../components/StatusBadge';
import { LeaseQuickFill, type LeaseHit } from '../components/LeaseQuickFill';
import { ListPage } from '../components/ListPage';
import { listState } from '../components/listPageContract';
import { listMeta } from '../components/listPageContract';
import { TableCard } from '../components/TableCard';
import { EmptyState } from '../components/EmptyState';
import { CustomerQuickCreateDialog } from '../components/CustomerQuickCreateDialog';
import { UnitQuickCreateDialog } from '../components/UnitQuickCreateDialog';
import { EditDialogFooter } from '../components/MasterDataDialogs';
import { PortalImportModal } from '../components/PortalImportModal';
import { useGemerkt } from '../lib/gemerkt';

export function OrdersPage() {
  // Tobias 2026-09-25: Filter bleiben beim Wechsel der Seite erhalten.
  const [search, setSearch] = useGemerkt('auftraege.suche', '');
  const [status, setStatus] = useGemerkt<string | null>('auftraege.status', null);
  const [createOpen, setCreateOpen] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const [unitCreateOpen, setUnitCreateOpen] = useState(false);
  // Steht der Auftraggeber noch nicht im Stamm, wird er hier angelegt statt
  // den halb gefüllten Auftrag zu verwerfen (Faruq 2026-09, Sage-Übernahme).
  const [customerCreateOpen, setCustomerCreateOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [values, setValues] = useState({
    customerId: '',
    title: '',
    description: '',
    unitId: '',
    bestellnummer: '',
    agreedTotalEuro: '' as number | string,
    commissionType: DEFAULT_COMMISSION_TYPE as string,
    // Kundentest 2026-08 (Tobias): Leerwohnung — der Monteur bekommt am
    // Auftrag die Aufnahme-Maske, das Büro sieht sie in der Auftragsakte.
    isEmptyApartment: false,
  });
  // MV-Schnellanlage (Kundentest 2026-08, Faruq): der gewählte Treffer bleibt
  // stehen, seine Mieterdaten werden gezeigt — und Lücken lassen sich hier
  // nachtragen; beim Anlegen wandern sie an die Einheit (= Mietverhältnis).
  const [lease, setLease] = useState<LeaseHit | null>(null);
  const [mieter, setMieter] = useState({ name: '', phone: '', email: '' });
  const navigate = useNavigate();
  useNeuParam(() => setCreateOpen(true));
  const queryClient = useQueryClient();
  // Kundenakte-Chip springt mit ?customerId=… hierher (Faruq 2026-08-17).
  const routeSearch = useSearch({ strict: false }) as { customerId?: string };
  const customerFilter = routeSearch.customerId ?? null;

  // Vollanalyse S5 (M19-Muster): Suche debounced statt ein Request pro
  // Tastendruck; placeholderData hält die Tabelle beim Tippen stehen.
  const [debouncedSearch] = useDebouncedValue(search.trim(), 250);
  const orders = useOrdersControllerList(
    {
      search: debouncedSearch || undefined,
      status: (status as never) ?? undefined,
      customerId: customerFilter ?? undefined,
      take: LISTE_ALLE,
    },
    { query: { placeholderData: keepPreviousData } },
  );
  // Vollanalyse S5: Kunden-Select ohne Suchverdrahtung → bewusst über der
  // 200er-Kappung anfordern (M19-Konvention, max. 1000).
  const customers = useCustomersControllerList({ archived: 'false', take: 1000 });
  const filterCustomerName = customerFilter
    ? (customers.data?.find((k) => k.id === customerFilter)?.displayName ?? 'Kunde')
    : null;
  const units = useCustomersControllerUnits(values.customerId, undefined, {
    query: { enabled: !!values.customerId },
  });
  // P5-C §1: „Kunde hat Objekte" — die Auftragsanlage fragt die
  // Beauftragungsart dann aktiv ab (Emphase am Select).
  const customerHasUnits = (units.data?.length ?? 0) > 0;

  // Mieterdaten am Mietverhältnis nachtragen (MV-Schnellanlage).
  const updateUnit = useCustomersControllerUpdateUnit<ApiError>();
  const create = useOrdersControllerCreate<ApiError>({
    mutation: {
      onSuccess: (order) => {
        void queryClient.invalidateQueries({ queryKey: getOrdersControllerListQueryKey() });
        setCreateOpen(false);
        void navigate({ to: '/orders/$orderId', params: { orderId: order.id } });
      },
    },
  });

  const submit = () => {
    // Kundentest 2026-08 (Faruq): geänderte/ergänzte Mieterdaten gehören ans
    // MIETVERHÄLTNIS, nicht an den Auftrag — sie stehen beim nächsten Mal
    // sofort wieder da. Ein Fehler hier darf die Auftragsanlage nicht kippen.
    if (lease) {
      const neu = {
        tenantName: mieter.name.trim() || null,
        tenantPhone: mieter.phone.trim() || null,
        tenantEmail: mieter.email.trim() || null,
      };
      const geaendert =
        neu.tenantName !== (lease.tenantName ?? null) ||
        neu.tenantPhone !== (lease.tenantPhone ?? null) ||
        neu.tenantEmail !== (lease.tenantEmail ?? null);
      if (geaendert) {
        updateUnit.mutate({ id: lease.unitId, data: neu as never });
      }
    }
    create.mutate({
      data: {
        customerId: values.customerId,
        title: values.title.trim(),
        description: values.description.trim() || undefined,
        unitId: values.unitId || undefined,
        commissionType: values.commissionType as never,
        isEmptyApartment: values.isEmptyApartment,
        externalRefs: values.bestellnummer.trim()
          ? { Bestellnummer: values.bestellnummer.trim() }
          : {},
        agreedTotalCents: values.agreedTotalEuro
          ? Math.round(Number(values.agreedTotalEuro) * 100)
          : undefined,
      },
    });
  };

  // D-W1 QS-3: der Zähler steht nur noch im Kartenfuß; die Kopf-Meta trägt
  // Beschreibung und aktive Filter (Vollanalyse S6: gemeinsamer listMeta-Helfer).
  const meta = listMeta(
    !!orders.data,
    'Alle Aufträge des Betriebs',
    status ? `Status ${ORDER_STATUS_LABELS[status]}` : null,
    search ? `Suche „${search}“` : null,
  );

  // D-W1 Welle 2 (A2-13, W2-B4): Seite auf das ListPage-Gerüst migriert —
  // die vier Zustände (Laden/Fehler/Leer/Gefüllt) trägt das Gerüst.
  const seiten = useSeiten(orders.data, 'auftraege');
  return (
    <>
      <ListPage
        title="Aufträge"
        meta={meta}
        action={
          <Group gap="sm">
            <Button
              variant="default"
              onClick={() => setPortalOpen(true)}
              data-testid="order-portal-import"
            >
              Auftrag importieren
            </Button>
            <Button onClick={() => setCreateOpen(true)} data-testid="order-create">
              Neuer Auftrag
            </Button>
          </Group>
        }
        filters={
          <>
            <TextInput
              placeholder="Suche (Nummer, Titel, Kunde, Name, Adresse, Referenz)…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={300}
            />
            <Select
              placeholder="Status"
              clearable
              data={selectOptions(ORDER_STATUS_LABELS)}
              value={status}
              onChange={setStatus}
              w={180}
            />
            {customerFilter && (
              <Button
                variant="light"
                size="xs"
                rightSection="×"
                onClick={() => void navigate({ to: '/orders' })}
                data-testid="orders-clear-customer"
              >
                Kunde: {filterCustomerName}
              </Button>
            )}
          </>
        }
        state={listState(orders, orders.data?.length === 0)}
        errorText="Aufträge konnten nicht geladen werden."
        empty={
          <TableCard testId="orders-card">
            <EmptyState
              text={
                search || status
                  ? 'Keine Aufträge für diese Filter gefunden.'
                  : 'Noch keine Aufträge angelegt.'
              }
              action={<Button onClick={() => setCreateOpen(true)}>Neuer Auftrag</Button>}
            />
          </TableCard>
        }
      >
        <TableCard
          testId="orders-card"
          footer={
            <TablePager p={seiten} einzahl="Auftrag" mehrzahl="Aufträge" testId="orders-pager" />
          }
        >
          {/* Adresse (mit dem Titel darunter) ist die einzige Spalte variabler
              Laenge — sie traegt die Kuerzung, damit Name, Telefon und Status
              IMMER vollstaendig lesbar bleiben (Faruq 2026-08-29). Regeln in
              werkbank.css. Faruq 2026-10-30: eigene Titel-Spalte entfaellt. */}
          <Table highlightOnHover className="wb-orders-table">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nummer</Table.Th>
                {/* Faruq 2026-09-03: Anlagedatum. */}
                <Table.Th>Angelegt</Table.Th>
                <Table.Th className="wb-col-flex">Adresse</Table.Th>
                {/* Faruq 2026-09-03: Wohnung (Label der Einheit, z. B. 6. OG R). */}
                <Table.Th>Wohnung</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Telefon</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Referenzen</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {seiten.sichtbar.map((o) => (
                <Table.Tr
                  key={o.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    void navigate({ to: '/orders/$orderId', params: { orderId: o.id } })
                  }
                >
                  <Table.Td>
                    <Text fw={500} className="wb-tnum">
                      {o.number}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" className="wb-tnum">
                      {formatDateDE(o.createdAt)}
                    </Text>
                  </Table.Td>
                  <Table.Td
                    className="wb-col-flex"
                    title={[o.site, o.title].filter(Boolean).join('\n')}
                  >
                    <Text size="sm" truncate>
                      {o.site ?? '—'}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                      {o.title}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{o.unitLabel ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{o.tenantName ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {o.tenantPhone ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <StatusBadge status={o.status} />
                      {o.adHoc && <StatusBadge status="ADHOC" />}
                      {o.isEmergency && <StatusBadge status="EMERGENCY" />}
                      {/* P2-G §3: On-hold-Kennzeichnung — das Büro sieht es sofort. */}
                      {o.holdInfo && (
                        <StatusBadge
                          status="HOLD"
                          label={`On-hold: ${holdReasonLabel(o.holdInfo.reason)}`}
                          data-testid={`order-hold-badge-${o.number}`}
                        />
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {/* D-W1 AU-2: Referenzen als leiser Chip statt Versalien-Outline. */}
                    {Object.entries(o.externalRefs).map(([k, v]) => (
                      <Badge
                        key={k}
                        className="wb-ref-badge"
                        variant="light"
                        color="gray"
                        size="sm"
                        mr={4}
                      >
                        {k}: {v}
                      </Badge>
                    ))}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </TableCard>
      </ListPage>

      <PortalImportModal opened={portalOpen} onClose={() => setPortalOpen(false)} />

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Neuer Auftrag"
        size="lg"
      >
        <Stack gap="md">
          {/* MV-Schnellanlage (Tobias-Fluss): die Mietvertragsnummer füllt
              Kunde (Rechnungsempfänger = Eigentümer, z. B. GWH) und Wohnung;
              die Beauftragungsart springt auf GWH-Beauftragung, solange sie
              noch auf dem Standard steht — alles bleibt änderbar. */}
          <LeaseQuickFill
            testId="order-lease-quick-fill"
            onPick={(hit) => {
              setLease(hit);
              if (!hit) {
                setMieter({ name: '', phone: '', email: '' });
                return;
              }
              setMieter({
                name: hit.tenantName ?? '',
                phone: hit.tenantPhone ?? '',
                email: hit.tenantEmail ?? '',
              });
              setValues({
                ...values,
                customerId: hit.customerId,
                unitId: hit.unitId,
                // Über die Mietvertragsnummer angelegt ⇒ IMMER
                // Mieterdirektbeauftragung (Faruq, Kundentest 2026-08).
                commissionType: 'MIETERDIREKT',
              });
            }}
          />
          {lease && (
            <Stack gap={4} data-testid="order-lease-tenant">
              <Text size="sm" fw={600}>
                Mieter zu {lease.leaseNumber}
              </Text>
              <Text size="xs" c="dimmed">
                {lease.tenantName || lease.tenantPhone || lease.tenantEmail
                  ? 'Am Mietverhältnis hinterlegt — Änderungen werden dort gespeichert.'
                  : 'Noch keine Mieterdaten hinterlegt. Was Sie hier eintragen, wird am Mietverhältnis gespeichert.'}
              </Text>
              <Group grow align="flex-start">
                <TextInput
                  label="Name"
                  value={mieter.name}
                  onChange={(e) => setMieter({ ...mieter, name: e.currentTarget.value })}
                  data-testid="order-tenant-name"
                />
                <TextInput
                  label="Telefon"
                  value={mieter.phone}
                  onChange={(e) => setMieter({ ...mieter, phone: e.currentTarget.value })}
                  data-testid="order-tenant-phone"
                />
                <TextInput
                  label="E-Mail"
                  value={mieter.email}
                  onChange={(e) => setMieter({ ...mieter, email: e.currentTarget.value })}
                  data-testid="order-tenant-email"
                />
              </Group>
            </Stack>
          )}
          {/* D-W1 Welle 3 (Dialoggrößen): die lg-Breite wird zweispaltig
              eingelöst — Kunde und Leistungsort als Paar, Referenz und Summe
              als Paar; der Dialog wird kürzer statt breiter als nötig. */}
          <Group grow align="flex-start">
            <Stack gap={4}>
              <Select
                label="Kunde"
                required
                searchable
                nothingFoundMessage="Kein Kunde — unten neu anlegen"
                data={customers.data?.map((c) => ({ value: c.id, label: c.displayName })) ?? []}
                value={values.customerId || null}
                searchValue={customerSearch}
                onSearchChange={setCustomerSearch}
                onChange={(v) => setValues({ ...values, customerId: v ?? '', unitId: '' })}
                data-testid="order-customer"
              />
              {/* Kein Treffer im Stamm? Dann von hier aus anlegen — der
                  Suchbegriff wandert als Namensvorschlag mit. */}
              <Group justify="flex-start">
                <Button
                  variant="subtle"
                  size="compact-xs"
                  onClick={() => setCustomerCreateOpen(true)}
                  data-testid="customer-quickcreate-open"
                >
                  + Neuen Kunden anlegen
                </Button>
              </Group>
            </Stack>
            <Stack gap={4}>
              <Select
                label="Wohnung (Leistungsort, optional)"
                description="Gefiltert auf Gebäude des gewählten Kunden"
                searchable
                clearable
                disabled={!values.customerId}
                data={units.data?.map((u) => ({ value: u.id, label: u.label })) ?? []}
                value={values.unitId || null}
                onChange={(v) => setValues({ ...values, unitId: v ?? '' })}
                data-testid="order-unit"
              />
              {/* P5-C §3: Schnellanlage ohne den Auftragsfluss zu verlassen. */}
              <Group justify="flex-start">
                <Button
                  variant="subtle"
                  size="compact-xs"
                  disabled={!values.customerId}
                  onClick={() => setUnitCreateOpen(true)}
                  data-testid="unit-quickcreate-open"
                >
                  + Neue Einheit anlegen
                </Button>
              </Group>
            </Stack>
          </Group>
          {/* P5-C §1: Beauftragungsart. Hat der Kunde Objekte (Einheiten),
              fragt die Anlage aktiv ab — sonst bleibt der Default „Sonstige". */}
          <Select
            label="Beauftragungsart"
            description={
              customerHasUnits ? 'Kunde mit Objekten — bitte Beauftragungsart wählen' : undefined
            }
            allowDeselect={false}
            data={COMMISSION_TYPE_OPTIONS}
            value={values.commissionType}
            onChange={(v) => setValues({ ...values, commissionType: v ?? DEFAULT_COMMISSION_TYPE })}
            data-testid="order-commission-type"
          />
          {/* Kundentest 2026-08 (Tobias): Leerwohnung. Ist der Haken gesetzt,
              öffnet die App dem Monteur die Aufnahme-Maske (Eingangstür,
              Zimmertüren, Fenster, Rollladen, neue Türen); das Büro sieht die
              Aufnahmen im Auftrag. */}
          <Checkbox
            label="Leerwohnung"
            description="Der Monteur erfasst am Auftrag eine Leerwohnungs-Aufnahme"
            checked={values.isEmptyApartment}
            onChange={(e) => setValues({ ...values, isEmptyApartment: e.currentTarget.checked })}
            data-testid="order-empty-apartment"
          />
          <TextInput
            label="Titel"
            required
            value={values.title}
            onChange={(e) => setValues({ ...values, title: e.currentTarget.value })}
            data-testid="order-title"
          />
          <Textarea
            label="Beschreibung (optional)"
            value={values.description}
            onChange={(e) => setValues({ ...values, description: e.currentTarget.value })}
          />
          <Group grow>
            <TextInput
              label="Bestellnummer (optional)"
              value={values.bestellnummer}
              onChange={(e) => setValues({ ...values, bestellnummer: e.currentTarget.value })}
              data-testid="order-ref"
            />
            <EuroInput
              label="Vereinbarte Auftragssumme netto (€, optional)"
              value={values.agreedTotalEuro}
              onChange={(v) => setValues({ ...values, agreedTotalEuro: v })}
            />
          </Group>
          <FormError error={create.error} />
          <EditDialogFooter
            error={null}
            onCancel={() => setCreateOpen(false)}
            onSave={submit}
            saving={create.isPending}
            saveTestId="order-save"
            saveLabel="Anlegen"
            saveDisabled={!values.customerId || !values.title.trim()}
          />
        </Stack>
      </Modal>

      {/* P5-C §3: Schnellanlage-Dialog; die neue Einheit wird sofort im
          Auftragsformular ausgewählt. */}
      <CustomerQuickCreateDialog
        opened={customerCreateOpen}
        onClose={() => setCustomerCreateOpen(false)}
        initialName={customerSearch}
        onCreated={(kunde) => {
          setValues((v) => ({ ...v, customerId: kunde.id, unitId: '' }));
          setCustomerSearch(kunde.displayName);
        }}
      />
      <UnitQuickCreateDialog
        opened={unitCreateOpen}
        onClose={() => setUnitCreateOpen(false)}
        customerId={values.customerId || undefined}
        onCreated={(unit) => setValues((v) => ({ ...v, unitId: unit.id }))}
      />
    </>
  );
}
